import React, { useEffect, useState, useMemo, useRef } from 'react';
import api from '../services/api';

// ─── Sabitler ─────────────────────────────────────────────
const TUR_RENK: Record<string, string> = {
  genel_mudur:   '#1a3a5c',
  gm_yardimcisi: '#1f618d',
  denetim:       '#6c3483',
  yardimci:      '#7d6608',
  daire:         '#1a5e35',
  sube:          '#4a4a4a',
  isletme:       '#117a65',
};
const TUR_AD: Record<string, string> = {
  genel_mudur:   'Genel Müdürlük',
  gm_yardimcisi: 'GM Yardımcısı',
  denetim:       'Denetim Birimi',
  yardimci:      'Yardımcı Birim',
  daire:         'Daire Başkanlığı',
  sube:          'Şube Müdürlüğü',
  isletme:       'Tarım İşletmesi',
};
const TUR_LISTE_SIRA = ['genel_mudur','gm_yardimcisi','denetim','yardimci','daire','sube','isletme'];

// ─── Ağaç oluşturucu ──────────────────────────────────────
function buildTree(flat: any[]): any[] {
  const map: Record<number, any> = {};
  flat.forEach(b => { map[b.id] = { ...b, _ch: [] }; });
  const roots: any[] = [];
  flat.forEach(b => {
    if (b.parent_id && map[b.parent_id]) map[b.parent_id]._ch.push(map[b.id]);
    else roots.push(map[b.id]);
  });
  const sort = (n: any) => { n._ch.sort((a: any, b: any) => a.sira - b.sira); n._ch.forEach(sort); };
  roots.sort((a, b) => a.sira - b.sira);
  roots.forEach(sort);
  return roots;
}

// ─── Ağaç Düğümü ──────────────────────────────────────────
function DugumSatir({ node, seciliId, onSec, acik, onToggle, seviye = 0 }: {
  node: any; seciliId: number | null; onSec: (b: any) => void;
  acik: Set<number>; onToggle: (id: number) => void; seviye?: number;
}) {
  const var_cocuk = node._ch.length > 0;
  const isAcik = acik.has(node.id);
  const isSecili = seciliId === node.id;
  const renk = TUR_RENK[node.tur] || '#555';

  return (
    <div>
      <div
        onClick={() => onSec(node)}
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: `5px 8px 5px ${8 + seviye * 14}px`,
          cursor: 'pointer', borderRadius: 5, marginBottom: 1,
          background: isSecili ? renk + '18' : 'transparent',
          borderLeft: isSecili ? `3px solid ${renk}` : '3px solid transparent',
        }}
        onMouseEnter={e => { if (!isSecili) (e.currentTarget as HTMLElement).style.background = '#f5f6f7'; }}
        onMouseLeave={e => { if (!isSecili) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        <span
          style={{ width: 14, fontSize: 10, color: '#aaa', flexShrink: 0, textAlign: 'center', cursor: var_cocuk ? 'pointer' : 'default' }}
          onClick={e => { if (var_cocuk) { e.stopPropagation(); onToggle(node.id); } }}
        >
          {var_cocuk ? (isAcik ? '▼' : '▶') : '·'}
        </span>
        <span style={{ fontSize: 13 }}>{node.ikon || '📁'}</span>
        <span style={{ fontSize: 12, color: isSecili ? renk : '#2d2d2d', fontWeight: isSecili ? 700 : 400, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {node.kisaltma || node.ad.substring(0, 22)}
        </span>
        {node.personel_sayisi > 0 && (
          <span style={{ fontSize: 10, color: '#aaa', background: '#f0f0f0', borderRadius: 8, padding: '1px 6px', flexShrink: 0 }}>
            {node.personel_sayisi}
          </span>
        )}
      </div>
      {var_cocuk && isAcik && node._ch.map((c: any) => (
        <DugumSatir key={c.id} node={c} seciliId={seciliId} onSec={onSec} acik={acik} onToggle={onToggle} seviye={seviye + 1} />
      ))}
    </div>
  );
}

// ─── Ana Bileşen ───────────────────────────────────────────
export default function OrgYapisi() {
  const [birimler, setBirimler] = useState<any[]>([]);
  const [secili, setSecili] = useState<any>(null);
  const [acik, setAcik] = useState<Set<number>>(new Set([1, 2, 3, 4]));
  const [gorunum, setGorunum] = useState<'agac' | 'liste'>('agac');
  const [editModal, setEditModal] = useState(false);
  const [mudurModal, setMudurModal] = useState(false);
  const [editForm, setEditForm] = useState<any>({});
  const [personelArama, setPersonelArama] = useState('');
  const [personelSonuclar, setPersonelSonuclar] = useState<any[]>([]);
  const [araYukleniyor, setAraYukleniyor] = useState(false);
  const [hata, setHata] = useState('');
  const araTimer = useRef<any>(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = ['super_admin', 'gm_yonetici'].includes(user.rol);

  const yukle = async () => {
    const r = await api.get('/api/org/');
    setBirimler(r.data);
    setSecili((prev: any) => {
      if (prev) return r.data.find((b: any) => b.id === prev.id) || prev;
      return r.data.find((b: any) => b.tur === 'genel_mudur') || r.data[0] || null;
    });
  };

  useEffect(() => { yukle(); }, []);

  const agac = useMemo(() => buildTree(birimler), [birimler]);

  const toggle = (id: number) =>
    setAcik(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const mudurAra = (q: string) => {
    setPersonelArama(q);
    if (araTimer.current) clearTimeout(araTimer.current);
    if (q.length < 2) { setPersonelSonuclar([]); setAraYukleniyor(false); return; }
    const temiz = q.replace(/\D/g, '');
    const ara = async () => {
      setAraYukleniyor(true);
      try {
        const params = temiz.length === 11 ? { tc: temiz } : { q };
        const r = await api.get('/api/org/personel-ara', { params });
        setPersonelSonuclar(r.data);
      } catch { setPersonelSonuclar([]); }
      finally { setAraYukleniyor(false); }
    };
    if (temiz.length === 11) ara();
    else { setAraYukleniyor(true); araTimer.current = setTimeout(ara, 200); }
  };

  const mudurAta = async (personelId: number | null) => {
    setHata('');
    try {
      await api.put(`/api/org/${secili.id}/mudur`, { personel_id: personelId });
      await yukle();
      setMudurModal(false);
      setPersonelArama('');
      setPersonelSonuclar([]);
    } catch (e: any) {
      setHata(e.response?.data?.detail || 'Hata oluştu');
    }
  };

  const birimSil = async () => {
    if (!secili) return;
    if (!window.confirm(`"${secili.ad}" birimini silmek istediğinizden emin misiniz?\nAlt birimleri olan birimler silinemez.`)) return;
    try {
      await api.delete(`/api/org/${secili.id}`);
      setSecili(null);
      await yukle();
    } catch (e: any) {
      alert('Silme hatası: ' + (e.response?.data?.detail || 'Hata oluştu'));
    }
  };

  const birimKaydet = async () => {
    if (!editForm.ad?.trim()) { setHata('Birim adı zorunludur'); return; }
    if (!editForm.tur) { setHata('Birim türü zorunludur'); return; }
    if (['daire', 'sube'].includes(editForm.tur) && !editForm.parent_id) { setHata('Bağlı olduğu birim seçilmelidir'); return; }
    setHata('');
    try {
      if (editForm.id) await api.put(`/api/org/${editForm.id}`, editForm);
      else await api.post('/api/org/', editForm);
      await yukle();
      setEditModal(false);
    } catch (e: any) { setHata(e.response?.data?.detail || 'Hata oluştu'); }
  };

  const secilenRenk = secili ? (TUR_RENK[secili.tur] || '#555') : '#1a5276';

  const gruplar = useMemo(() => {
    const g: Record<string, any[]> = {};
    TUR_LISTE_SIRA.forEach(t => { g[t] = []; });
    birimler.forEach(b => { if (g[b.tur]) g[b.tur].push(b); });
    return g;
  }, [birimler]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 110px)' }}>

      {/* Başlık */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexShrink: 0 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>🏛️ TİGEM Kurum Yapısı</h1>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#888' }}>
            Organizasyon hiyerarşisi · Müdür bilgisi Personel modülünden · İşletme bilgisi Tanımlar'dan okunur
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {(['agac', 'liste'] as const).map(g => (
            <button key={g} onClick={() => setGorunum(g)} style={{
              padding: '6px 14px', borderRadius: 5, border: '1px solid #ddd', cursor: 'pointer', fontSize: 13,
              background: gorunum === g ? '#1a5276' : '#fff', color: gorunum === g ? '#fff' : '#333',
            }}>
              {g === 'agac' ? '🌳 Ağaç' : '📋 Liste'}
            </button>
          ))}
          <button onClick={async () => {
            const BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000');
            const r = await fetch(`${BASE}/api/org/excel-export`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            if (!r.ok) { alert('İndirme hatası'); return; }
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = 'org_yapisi.xlsx'; a.click();
            URL.revokeObjectURL(url);
          }} style={{ padding: '6px 14px', borderRadius: 5, background: '#1a5276', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13 }}>
            📥 Excel İndir
          </button>
          {isAdmin && (
            <button onClick={() => { setEditForm({ tur: 'sube', sira: 0 }); setHata(''); setEditModal(true); }}
              style={{ padding: '6px 14px', borderRadius: 5, background: '#27ae60', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13 }}>
              + Birim Ekle
            </button>
          )}
        </div>
      </div>

      {gorunum === 'agac' ? (
        <div style={{ display: 'flex', gap: 10, flex: 1, overflow: 'hidden' }}>

          {/* Sol — Ağaç */}
          <div style={{ width: 265, background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'auto', padding: '10px 6px', flexShrink: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#ccc', letterSpacing: 1, padding: '0 8px', marginBottom: 8 }}>
              KURUM AĞACI
            </div>
            {agac.map(node => (
              <DugumSatir key={node.id} node={node} seciliId={secili?.id ?? null}
                onSec={b => { setSecili(b); setAcik(prev => new Set(Array.from(prev).concat([b.parent_id, b.id]).filter(Boolean) as number[])); }}
                acik={acik} onToggle={toggle} />
            ))}
          </div>

          {/* Sağ — Detay */}
          <div style={{ flex: 1, background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'auto', padding: 22 }}>
            {secili ? (
              <>
                {/* Başlık satırı */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 34 }}>{secili.ikon || '📁'}</span>
                    <div>
                      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111' }}>{secili.ad}</h2>
                      <div style={{ display: 'flex', gap: 6, marginTop: 5 }}>
                        <span style={{ background: secilenRenk, color: '#fff', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
                          {TUR_AD[secili.tur] || secili.tur}
                        </span>
                        {secili.kisaltma && (
                          <span style={{ background: '#f0f0f0', color: '#444', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>
                            {secili.kisaltma}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {isAdmin && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => { setEditForm({ ...secili }); setHata(''); setEditModal(true); }}
                        style={{ padding: '6px 14px', background: '#2980b9', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 13 }}>
                        Düzenle
                      </button>
                      <button onClick={birimSil}
                        style={{ padding: '6px 14px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 13 }}>
                        Sil
                      </button>
                    </div>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  {/* Müdür kartı */}
                  <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 16, border: '1px solid #e9ecef' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#aaa', marginBottom: 10, letterSpacing: 1 }}>YETKİLİ / MÜDÜR</div>
                    {secili.mudur_ad ? (
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 700 }}>{secili.mudur_ad}</div>
                        <div style={{ fontSize: 11, marginTop: 4, color: secili.mudur_personel_id ? '#27ae60' : '#e67e22' }}>
                          {secili.mudur_personel_id ? '✔ Personel modülüne bağlı' : '⚠ Sadece metin — personel kaydı yok'}
                        </div>
                      </div>
                    ) : (
                      <div style={{ color: '#ccc', fontSize: 13, fontStyle: 'italic' }}>Atanmamış</div>
                    )}
                    {isAdmin && (
                      <button onClick={() => { setPersonelArama(''); setPersonelSonuclar([]); setHata(''); setMudurModal(true); }}
                        style={{ marginTop: 12, padding: '5px 12px', borderRadius: 5, background: '#fff', border: '1px solid #ddd', cursor: 'pointer', fontSize: 12 }}>
                        {secili.mudur_personel_id ? '↩ Değiştir' : '+ Müdür Ata'}
                      </button>
                    )}
                  </div>

                  {/* İstatistik kartı */}
                  <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 16, border: '1px solid #e9ecef' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#aaa', marginBottom: 10, letterSpacing: 1 }}>BİRİM İSTATİSTİKLERİ</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                        <span style={{ color: '#666' }}>Bağlı Personel</span>
                        <strong style={{ color: '#1a5276' }}>{secili.personel_sayisi || 0} kişi</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                        <span style={{ color: '#666' }}>Alt Birim</span>
                        <strong>{birimler.filter(b => b.parent_id === secili.id).length} adet</strong>
                      </div>
                      {secili.telefon && <div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>📞 {secili.telefon}</div>}
                      {secili.email && <div style={{ fontSize: 12, color: '#555' }}>✉️ {secili.email}</div>}
                    </div>
                  </div>

                  {/* İşletme bilgileri */}
                  {secili.tur === 'isletme' && secili.il && (
                    <div style={{ gridColumn: '1 / -1', background: '#e8f8f5', borderRadius: 8, padding: 16, border: '1px solid #a3e4d7' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#0e6655', marginBottom: 10, letterSpacing: 1 }}>
                        İŞLETME BİLGİLERİ · Tanımlar modülünden
                      </div>
                      <div style={{ display: 'flex', gap: 32, fontSize: 13 }}>
                        <div><span style={{ color: '#666' }}>İl: </span><strong>{secili.il}</strong></div>
                        {secili.ilce && <div><span style={{ color: '#666' }}>İlçe: </span><strong>{secili.ilce}</strong></div>}
                      </div>
                    </div>
                  )}

                  {/* Alt birimler */}
                  {(() => {
                    const alt = birimler.filter(b => b.parent_id === secili.id);
                    if (!alt.length) return null;
                    return (
                      <div style={{ gridColumn: '1 / -1', background: '#f8f9fa', borderRadius: 8, padding: 16, border: '1px solid #e9ecef' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#aaa', marginBottom: 10, letterSpacing: 1 }}>
                          ALT BİRİMLER ({alt.length})
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {alt.map(b => {
                            const r = TUR_RENK[b.tur] || '#555';
                            return (
                              <span key={b.id}
                                onClick={() => { setSecili(b); setAcik(prev => new Set(Array.from(prev).concat([b.id]))); }}
                                style={{ padding: '5px 14px', borderRadius: 16, background: r + '15', border: `1px solid ${r}35`, fontSize: 12, cursor: 'pointer', color: r, fontWeight: 600 }}>
                                {b.ikon} {b.kisaltma || b.ad.substring(0, 16)}
                                {b.personel_sayisi > 0 && <span style={{ marginLeft: 4, fontSize: 10, opacity: 0.7 }}>({b.personel_sayisi})</span>}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', color: '#ccc', marginTop: 80, fontSize: 15 }}>
                Sol panelden bir birim seçin
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Liste görünümü */
        <div style={{ flex: 1, overflow: 'auto' }}>
          {TUR_LISTE_SIRA.filter(tur => gruplar[tur]?.length > 0).map(tur => (
            <div key={tur} style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <div style={{ width: 4, height: 18, background: TUR_RENK[tur], borderRadius: 2 }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#444' }}>{TUR_AD[tur]}</span>
                <span style={{ fontSize: 12, color: '#bbb' }}>({gruplar[tur].length})</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                {gruplar[tur].map(b => {
                  const r = TUR_RENK[b.tur] || '#555';
                  return (
                    <div key={b.id}
                      onClick={() => { setSecili(b); setGorunum('agac'); setAcik(prev => new Set(Array.from(prev).concat([b.id, b.parent_id]).filter(Boolean) as number[])); }}
                      style={{ background: '#fff', borderLeft: `4px solid ${r}`, borderRadius: 6, padding: '12px 14px', cursor: 'pointer', border: `1px solid ${r}20`, borderLeftColor: r, borderLeftWidth: 4 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1a1a', marginBottom: 4 }}>
                        {b.ikon} {b.ad}
                      </div>
                      {b.mudur_ad && <div style={{ fontSize: 12, color: '#555' }}>👤 {b.mudur_ad}</div>}
                      {b.il && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>📍 {b.il}{b.ilce ? ` / ${b.ilce}` : ''}</div>}
                      {b.personel_sayisi > 0 && <div style={{ fontSize: 11, color: '#2980b9', marginTop: 2 }}>👥 {b.personel_sayisi} personel</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Müdür Atama Modali */}
      {mudurModal && secili && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setMudurModal(false)}>
          <div style={{ background: '#fff', borderRadius: 10, width: 500, padding: 24, boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>Müdür Ata</h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#888' }}>{secili.ad} · TC kimlik no önceliklidir</p>
            {hata && <div style={{ background: '#fdecea', color: '#c0392b', padding: '8px 12px', borderRadius: 5, marginBottom: 12, fontSize: 13 }}>{hata}</div>}
            <div style={{ position: 'relative', marginBottom: 16 }}>
              <input autoFocus value={personelArama} onChange={e => mudurAra(e.target.value)}
                placeholder="TC kimlik no veya ad / soyad / sicil no..."
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 5, fontSize: 14, boxSizing: 'border-box', outline: 'none' }} />
              {araYukleniyor && (
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Aranıyor...</div>
              )}
              {!araYukleniyor && personelArama.length >= 2 && personelSonuclar.length === 0 && (
                <div style={{ fontSize: 12, color: '#c0392b', marginTop: 4 }}>Kayıtlı personel bulunamadı.</div>
              )}
              {personelSonuclar.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ddd', borderRadius: 5, zIndex: 10, boxShadow: '0 6px 16px rgba(0,0,0,0.12)' }}>
                  {personelSonuclar.map((p: any) => (
                    <div key={p.id} onClick={() => mudurAta(p.id)}
                      style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f8f9fa')}
                      onMouseLeave={e => (e.currentTarget.style.background = '')}>
                      <strong>{p.ad_soyad}</strong>
                      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                        TC: <strong>{p.tc_kimlik_no || '—'}</strong>
                        <span style={{ marginLeft: 10 }}>Sicil: {p.sicil_no}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              {secili.mudur_personel_id ? (
                <button onClick={() => mudurAta(null)}
                  style={{ padding: '7px 14px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 13 }}>
                  Müdürü Kaldır
                </button>
              ) : <div />}
              <button onClick={() => { setMudurModal(false); setPersonelArama(''); setPersonelSonuclar([]); }}
                style={{ padding: '7px 16px', background: '#eee', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 13 }}>
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Birim Ekle/Düzenle Modali */}
      {editModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setEditModal(false)}>
          <div style={{ background: '#fff', borderRadius: 10, width: 500, padding: 24, boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>{editForm.id ? 'Birim Düzenle' : 'Yeni Birim'}</h3>
            {hata && <div style={{ background: '#fdecea', color: '#c0392b', padding: '8px 12px', borderRadius: 5, marginBottom: 12, fontSize: 13 }}>{hata}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[
                { label: 'Birim Adı *', key: 'ad', col: '1/-1', placeholder: '' },
                { label: 'Kısaltma', key: 'kisaltma', placeholder: 'ÖRN: BTDB' },
                { label: 'İkon', key: 'ikon', placeholder: '🏢' },
                { label: 'Telefon', key: 'telefon', placeholder: '' },
                { label: 'E-posta', key: 'email', placeholder: '' },
              ].map(({ label, key, col, placeholder }: any) => (
                <div key={key} style={col ? { gridColumn: col } : {}}>
                  <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>{label}</label>
                  <input value={editForm[key] || ''} onChange={e => setEditForm({ ...editForm, [key]: e.target.value })}
                    placeholder={placeholder}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 5, fontSize: 14, boxSizing: 'border-box' }} />
                </div>
              ))}
              <div>
                <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Tür *</label>
                <select value={editForm.tur || ''} onChange={e => {
                  const tur = e.target.value;
                  // Denetim/yardımcı → otomatik parent_id=1 (Genel Müdürlük)
                  const autoParent = (tur === 'denetim' || tur === 'yardimci') ? 1 : editForm.parent_id;
                  setEditForm({ ...editForm, tur, parent_id: autoParent });
                }}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 5, fontSize: 14 }}>
                  <option value="">Seçiniz</option>
                  <option value="genel_mudur">Genel Müdür</option>
                  <option value="gm_yardimcisi">Genel Müdür Yardımcısı</option>
                  <option value="denetim">Denetim Birimi</option>
                  <option value="yardimci">Yardımcı Birim</option>
                  <option value="daire">Daire Başkanlığı</option>
                  <option value="sube">Şube Müdürlüğü</option>
                </select>
              </div>
              {/* Üst birim seçimi — türe göre filtrelenir */}
              {editForm.tur && !['denetim', 'yardimci'].includes(editForm.tur) && (
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>
                    Bağlı Olduğu Birim *
                    <span style={{ fontWeight: 400, color: '#999', marginLeft: 6 }}>
                      {editForm.tur === 'daire' ? '(Genel Müdür Yardımcısı)' : '(Daire Başkanlığı)'}
                    </span>
                  </label>
                  <select value={editForm.parent_id || ''} onChange={e => setEditForm({ ...editForm, parent_id: Number(e.target.value) || undefined })}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 5, fontSize: 14 }}>
                    <option value="">Seçiniz</option>
                    {editForm.tur === 'daire'
                      ? birimler.filter(b => b.tur === 'gm_yardimcisi').map(b => (
                          <option key={b.id} value={b.id}>{b.ad}</option>
                        ))
                      : birimler.filter(b => b.tur === 'daire').map(b => (
                          <option key={b.id} value={b.id}>{b.ad}</option>
                        ))
                    }
                  </select>
                </div>
              )}
              {editForm.tur && ['denetim', 'yardimci'].includes(editForm.tur) && (
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Bağlı Olduğu Birim</label>
                  <div style={{ padding: '8px 10px', background: '#f5f7fa', border: '1px solid #ddd', borderRadius: 5, fontSize: 14, color: '#555' }}>
                    🏛️ TİGEM Genel Müdürlüğü (doğrudan bağlı)
                  </div>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button onClick={() => setEditModal(false)} style={{ padding: '8px 16px', background: '#eee', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 13 }}>İptal</button>
              <button onClick={birimKaydet} style={{ padding: '8px 18px', background: '#1a5276', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
